import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, CheckCircle2, XCircle, Clock, FileText, AlertCircle, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PILOT_TOKEN_KEY = 'powersure_pilot_token';

function authHeaders() {
  const t = localStorage.getItem(PILOT_TOKEN_KEY)
    || localStorage.getItem('admin_token')
    || localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function fmtPrice(q) {
  if (q.amount_cents != null) {
    try {
      return new Intl.NumberFormat('nl-NL', {
        style: 'currency',
        currency: q.currency || 'EUR',
        minimumFractionDigits: 2,
      }).format(q.amount_cents / 100);
    } catch {
      const eur = (q.amount_cents / 100).toFixed(2).replace('.', ',');
      return `€ ${eur}`;
    }
  }
  return q.amount_label || 'Prijs op aanvraag';
}

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' }); }
  catch { return iso; }
}

// Klantvriendelijke status-labels (per Phase 5 spec).
const STATUS_FRIENDLY = {
  waiting_customer: 'Wacht op uw keuze',
  approved:         'Goedgekeurd',
  rejected:         'Afgewezen',
  expired:          'Verlopen',
  in_review:        'In behandeling',
  requested:        'Aangevraagd',
  quote_received:   'Nieuw voorstel',
  new:              'Nieuw voorstel',
  reviewed:         'In behandeling',
  cancelled:        'Geannuleerd',
};

function StatusBadge({ status, label }) {
  // Dark variants — emerald/amber/rose/sky/slate
  const tone = {
    waiting_customer: 'bg-amber-500/15 text-amber-300 border-amber-400/30',
    approved:         'bg-emerald-500/15 text-emerald-300 border-emerald-400/30',
    rejected:         'bg-rose-500/15 text-rose-300 border-rose-400/30',
    expired:          'bg-white/8 text-white/65 border-white/15',
    in_review:        'bg-sky-500/15 text-sky-300 border-sky-400/30',
    requested:        'bg-sky-500/15 text-sky-300 border-sky-400/30',
    quote_received:   'bg-sky-500/15 text-sky-300 border-sky-400/30',
    new:              'bg-sky-500/15 text-sky-300 border-sky-400/30',
    reviewed:         'bg-sky-500/15 text-sky-300 border-sky-400/30',
    cancelled:        'bg-white/8 text-white/65 border-white/15',
  }[status] || 'bg-white/8 text-white/65 border-white/15';
  const friendly = STATUS_FRIENDLY[status] || label || status;
  return (
    <span
      className={`text-[10.5px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full border ${tone}`}
      data-testid={`quote-status-${status}`}
    >
      {friendly}
    </span>
  );
}

function QuoteCard({ quote, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmKind, setConfirmKind] = useState(null);
  const [reason, setReason] = useState('');

  const decide = async (kind) => {
    setBusy(true); setError('');
    try {
      const url = `${API}/app/quotes/${quote.quote_id}/${kind}`;
      const body = kind === 'reject' && reason ? { note: reason } : {};
      const res = await axios.post(url, body, { headers: authHeaders() });
      if (res.data?.quote) onChanged(res.data.quote);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false); setConfirmKind(null);
    }
  };

  const isWaiting = quote.status === 'waiting_customer';
  const isApproved = quote.status === 'approved';
  const isRejected = quote.status === 'rejected';
  const isExpired = quote.status === 'expired';

  return (
    <div
      className="rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-4 sm:p-5 space-y-3"
      data-testid={`quote-card-${quote.quote_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base sm:text-lg font-semibold tracking-tight text-white" style={{ fontFamily: 'Outfit' }}>
            {quote.title}
          </h3>
          {quote.installer_name && (
            <div className="text-xs text-white/55 mt-0.5">via {quote.installer_name}</div>
          )}
        </div>
        <StatusBadge status={quote.status} label={quote.status_label} />
      </div>
      <p className="text-sm text-white/80 leading-relaxed">{quote.summary}</p>
      {quote.description && (
        <p className="text-sm text-white/65 whitespace-pre-line leading-relaxed">{quote.description}</p>
      )}
      <div className="flex items-baseline gap-2">
        <span
          className="text-2xl font-semibold tracking-tight text-emerald-300"
          style={{ fontFamily: 'Outfit' }}
          data-testid="quote-amount"
        >
          {fmtPrice(quote)}
        </span>
        {quote.is_test_quote && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-300 border border-sky-400/30">
            voorbeeld
          </span>
        )}
      </div>
      {quote.valid_until && (
        <div className="text-xs text-white/55">Geldig tot {fmtDate(quote.valid_until)}</div>
      )}

      {isWaiting && !confirmKind && (
        <div className="flex gap-2 pt-1">
          <button
            onClick={() => setConfirmKind('approve')}
            data-testid="quote-approve-btn"
            className="flex-1 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 rounded-xl text-sm font-semibold shadow-lg shadow-emerald-900/40"
          >
            Goedkeuren
          </button>
          <button
            onClick={() => setConfirmKind('reject')}
            data-testid="quote-reject-btn"
            className="flex-1 px-4 py-2.5 bg-white/8 border border-white/15 hover:border-rose-400/40 text-white/85 rounded-xl text-sm font-medium"
          >
            Afwijzen
          </button>
        </div>
      )}

      {confirmKind === 'approve' && (
        <div
          className="rounded-xl bg-emerald-500/10 border border-emerald-400/30 p-3 space-y-2"
          data-testid="quote-confirm-approve"
        >
          <p className="text-sm text-white/90">Weet u zeker dat u deze offerte wilt goedkeuren?</p>
          <div className="flex gap-2">
            <button
              onClick={() => decide('approve')}
              disabled={busy}
              data-testid="quote-confirm-approve-btn"
              className="flex-1 px-3 py-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 rounded-md text-sm font-semibold disabled:opacity-50"
            >
              {busy ? 'Bezig…' : 'Ja, offerte goedkeuren'}
            </button>
            <button
              onClick={() => setConfirmKind(null)}
              className="px-3 py-2 bg-white/8 border border-white/15 text-white/85 rounded-md text-sm"
            >
              Terug
            </button>
          </div>
        </div>
      )}

      {confirmKind === 'reject' && (
        <div
          className="rounded-xl bg-rose-500/10 border border-rose-400/30 p-3 space-y-2"
          data-testid="quote-confirm-reject"
        >
          <p className="text-sm text-white/90">Wilt u deze offerte afwijzen? U zit nergens aan vast.</p>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="quote-reject-reason"
            className="w-full bg-white/8 border border-white/15 text-white/90 rounded-md p-2 text-sm"
          >
            <option value="">— reden (optioneel) —</option>
            <option value="te_duur">Te duur</option>
            <option value="niet_nu">Niet nu</option>
            <option value="meer_info">Ik wil eerst meer informatie</option>
            <option value="anders">Anders</option>
          </select>
          <div className="flex gap-2">
            <button
              onClick={() => decide('reject')}
              disabled={busy}
              data-testid="quote-confirm-reject-btn"
              className="flex-1 px-3 py-2 bg-rose-500 hover:bg-rose-400 text-rose-950 rounded-md text-sm font-semibold disabled:opacity-50"
            >
              {busy ? 'Bezig…' : 'Ja, offerte afwijzen'}
            </button>
            <button
              onClick={() => setConfirmKind(null)}
              className="px-3 py-2 bg-white/8 border border-white/15 text-white/85 rounded-md text-sm"
            >
              Terug
            </button>
          </div>
        </div>
      )}

      {isApproved && (
        <div
          className="rounded-xl bg-emerald-500/10 border border-emerald-400/30 p-3 text-sm flex items-start gap-2"
          data-testid="quote-approved-msg"
        >
          <CheckCircle2 className="w-5 h-5 text-emerald-300 flex-shrink-0" />
          <div>
            <strong className="text-white">U heeft deze offerte goedgekeurd.</strong>
            <div className="text-xs text-white/65 mt-0.5">Uw keuze is ontvangen. We nemen de volgende stap via Powersure.</div>
          </div>
        </div>
      )}
      {isRejected && (
        <div
          className="rounded-xl bg-white/5 border border-white/15 p-3 text-sm flex items-start gap-2"
          data-testid="quote-rejected-msg"
        >
          <XCircle className="w-5 h-5 text-white/55 flex-shrink-0" />
          <div>
            <strong className="text-white/90">U heeft deze offerte afgewezen.</strong>
            <div className="text-xs text-white/55 mt-0.5">Uw keuze is opgeslagen. U zit nergens aan vast.</div>
          </div>
        </div>
      )}
      {isExpired && (
        <div
          className="rounded-xl bg-white/5 border border-white/15 p-3 text-sm flex items-start gap-2 text-white/75"
          data-testid="quote-expired-msg"
        >
          <Clock className="w-5 h-5 text-white/55 flex-shrink-0" />
          <span>Deze offerte is verlopen.</span>
        </div>
      )}
      {error && (
        <div
          className="text-xs text-rose-200 bg-rose-500/10 border border-rose-400/30 rounded-md p-2 flex items-start gap-1"
          data-testid="quote-error"
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}
    </div>
  );
}

export default function QuotesTab() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [pilot, setPilot] = useState(null);
  const [attempt, setAttempt] = useState(0);

  const load = async () => {
    try {
      const [qRes, mRes] = await Promise.all([
        axios.get(`${API}/app/quotes`, { headers: authHeaders() }),
        axios.get(`${API}/pilot-devices/me`, { headers: authHeaders() })
          .catch(() => ({ data: { pilot: null } })),
      ]);
      setItems(qRes.data?.items || []);
      setPilot(mRes.data?.pilot || null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
      setItems([]);
    }
  };
  useEffect(() => { load(); }, []);

  // P0 demo race-fix: als pilot is_demo én items 0, doe automatisch 2 retries
  // (1s, 3s) voordat we de "Demo wordt voorbereid" tonen. Seed-write op
  // productie kan een fractie later beschikbaar zijn dan de eerste GET.
  useEffect(() => {
    if (!pilot?.is_demo) return;
    if (items === null) return;
    if (items.length > 0) return;
    if (attempt >= 2) return;
    const delay = attempt === 0 ? 1000 : 2500;
    const id = setTimeout(() => { setAttempt((n) => n + 1); load(); }, delay);
    return () => clearTimeout(id);
  }, [pilot, items, attempt]);

  const updateOne = (q) => setItems((prev) => (prev || []).map((it) => it.quote_id === q.quote_id ? q : it));

  if (items === null) {
    return <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-emerald-400" /></div>;
  }

  // Demo pilot maar items nog leeg én retry-budget nog niet uitgeput:
  // toon een rustige loading state ipv direct foutkaart.
  const demoStillLoading = pilot?.is_demo && items.length === 0 && attempt < 2;

  return (
    <div className="space-y-3" data-testid="quotes-list">
      {/* Header */}
      <header className="space-y-1">
        <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-white" style={{ fontFamily: 'Outfit' }}>
          Mijn offertes
        </h2>
        <p className="text-sm text-white/65 leading-relaxed">
          Bekijk uw voorstel en kies rustig uw volgende stap.
        </p>
      </header>

      {/* Demo banner — rustig in Powersure-stijl */}
      {items.length > 0 && items.some(q => q.is_demo) && (
        <div
          className="rounded-xl bg-sky-500/10 border border-sky-400/30 text-sky-200 p-3 text-sm flex items-start gap-2"
          data-testid="quotes-demo-banner"
        >
          <Sparkles className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <strong className="text-white">Voorbeeldweergave</strong>
            <div className="text-[12.5px] text-white/70 mt-0.5">Deze offertes zijn bedoeld om de Powersure-app te bekijken.</div>
          </div>
        </div>
      )}

      {/* Empty states */}
      {demoStillLoading && (
        <div
          className="rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-6 text-center"
          data-testid="quotes-demo-loading"
        >
          <Loader2 className="w-8 h-8 mx-auto text-emerald-300 animate-spin mb-2" />
          <h3 className="font-semibold text-white">Voorbeeld-offertes laden…</h3>
          <p className="text-sm text-white/65 mt-1">Een moment, we tonen direct uw demo.</p>
        </div>
      )}

      {!demoStillLoading && items.length === 0 && pilot?.is_demo && (
        <div
          className="rounded-2xl bg-amber-500/10 border border-amber-400/30 p-6 text-center"
          data-testid="quotes-demo-pending"
        >
          <AlertCircle className="w-10 h-10 mx-auto text-amber-300 mb-2" />
          <h3 className="font-semibold text-white">Demo wordt voorbereid</h3>
          <p className="text-sm text-white/70 mt-1 leading-relaxed">
            De voorbeeld-offertes zijn nog niet zichtbaar. Open de demo
            opnieuw vanuit het admin paneel (Pilot Access tab → &quot;Open demo-app&quot;).
          </p>
          <button
            onClick={() => { setAttempt(0); load(); }}
            data-testid="quotes-demo-retry"
            className="mt-3 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-amber-950 rounded-lg text-sm font-semibold"
          >
            Opnieuw proberen
          </button>
        </div>
      )}

      {items.length === 0 && !pilot?.is_demo && (
        <div
          className="rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-6 text-center"
          data-testid="quotes-empty"
        >
          <FileText className="w-10 h-10 mx-auto text-white/35 mb-2" />
          <h3 className="font-semibold text-white">Nog geen offerte</h3>
          <p className="text-sm text-white/65 mt-1 leading-relaxed max-w-sm mx-auto">
            Vraag een voorstel aan wanneer u een apparaat of maatregel wilt laten plaatsen — u ziet hier direct
            de prijs, geldigheid en kunt rustig kiezen.
          </p>
          {error && <p className="text-xs text-rose-200 mt-3">{String(error)}</p>}
        </div>
      )}

      {/* Quote cards */}
      {items.map((q) => <QuoteCard key={q.quote_id} quote={q} onChanged={updateOne} />)}
    </div>
  );
}
